import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { MissingTileImages, TileImageService, TileImageSlot } from '../../services/tile-image.service';

type RowState = 'pending' | 'running' | 'done' | 'failed';
interface Row extends MissingTileImages { selected: boolean; state: RowState; message?: string }

/** Superadmin-Dialog "Kachelbilder generieren": listet alle globalen Kacheln ohne Titelbild/Icon
 * mit vorgeschlagenen (editierbaren) Prompts und bebildert die ausgewählten nacheinander. Auf
 * Wunsch auch Kacheln mit vorhandenen Icons/Titelbildern — die alten Bilder werden dann nach
 * einer Bestätigung überschrieben. */
@Component({
    selector: 'app-tile-image-backfill', standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, DialogModule, TextareaModule],
    templateUrl: './tile-image-backfill.component.html',
    styleUrls: ['./tile-image-backfill.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TileImageBackfillComponent {
    @Input() public visible = false;
    @Output() public visibleChange = new EventEmitter<boolean>();
    /** Nach jeder gespeicherten Kachel — damit die Kachelliste die neuen Bilder zeigt. */
    @Output() public changed = new EventEmitter<void>();
    public rows: Row[] = [];
    public loading = false;
    public running = false;
    public error = '';
    /** Vorhandene Bilder dieser Slots neu generieren. */
    public replace: Record<TileImageSlot, boolean> = { icon: false, cover: false };
    /** Zweiter Schritt vor dem Start, wenn dabei vorhandene Bilder überschrieben werden. */
    public confirming = false;
    public readonly slotLabels: Record<TileImageSlot, string> = {
        cover: $localize`:@@tileImageCover:Titelbild`,
        icon: $localize`:@@tileImageIcon:Icon`,
    };
    private stopRequested = false;
    private readonly images = inject(TileImageService);
    private readonly cdr = inject(ChangeDetectorRef);

    public get selectedImages(): number {
        return this.rows.filter(row => row.selected && row.state !== 'done').reduce((sum, row) => sum + row.missing.length, 0);
    }

    /** Anzahl vorhandener Bilder, die der Start überschreiben würde. */
    public get replacedImages(): number {
        return this.rows.filter(row => row.selected && row.state !== 'done').reduce((sum, row) => sum + row.replaces.length, 0);
    }

    public async load(): Promise<void> {
        this.loading = true;
        this.error = '';
        this.confirming = false;
        this.cdr.markForCheck();
        try {
            const replace = (['icon', 'cover'] as const).filter(slot => this.replace[slot]);
            this.rows = (await this.images.missing(replace)).map(entry => ({ ...entry, selected: true, state: 'pending' }));
        } catch (error) {
            this.error = this.message(error);
        } finally {
            this.loading = false;
            this.cdr.markForCheck();
        }
    }

    public requestStart(): void {
        if (this.running) return;
        if (this.replacedImages) {
            this.confirming = true;
            return;
        }
        void this.start();
    }

    public async start(): Promise<void> {
        if (this.running) return;
        this.confirming = false;
        this.running = true;
        this.stopRequested = false;
        for (const row of this.rows) {
            if (this.stopRequested) break;
            if (!row.selected || row.state === 'done') continue;
            row.state = 'running';
            row.message = undefined;
            this.cdr.markForCheck();
            try {
                await this.images.fill(row.tileId, row.prompts, row.replaces);
                row.state = 'done';
                this.changed.emit();
            } catch (error) {
                row.state = 'failed';
                row.message = this.message(error);
            }
            this.cdr.markForCheck();
        }
        this.running = false;
        this.cdr.markForCheck();
    }

    /** Stoppt nach der laufenden Kachel — eine gestartete Generierung lässt sich nicht kostenfrei abbrechen. */
    public stop(): void { this.stopRequested = true; }

    public close(): void {
        this.stop();
        this.confirming = false;
        this.visible = false;
        this.visibleChange.emit(false);
    }

    private message(error: unknown): string {
        if (error instanceof HttpErrorResponse) {
            const body = error.error;
            if (typeof body === 'string' && body) return body;
            if (typeof body?.message === 'string') return body.message;
        }
        return $localize`:@@tileImageBackfillFailed:Die Bilder konnten nicht erstellt werden. Bitte später erneut versuchen.`;
    }
}
